/**
 * Order detail view. Full read of one commerce_orders row plus Sage-mirror
 * enrichment (partner address, phone, terms, last shipment date, YTD volume)
 * and line items pulled from the Faire envelope.
 *
 * Top-right action strip mirrors the orders list: Match, Ship, View in Faire,
 * Notes (with audit tab). Footer actions: Cancel (returns to the filtered
 * list view), Delete (soft-delete from Cockpit).
 */

import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { useSession } from '@/lib/useSession';
import { hasFaireAdmin, hasFaireViewer } from '@/lib/useFaireRuns';
import {
  faireOrderUrl,
  formatMoney,
  formatOrderNumber,
  useDeleteOrder,
  useOrderDetail,
  useShipOne,
  ORDER_STATUS_LABELS,
  SHIP_STATUS_LABELS,
  type ChannelAddress,
  type PartnerEnrichment,
} from '@/lib/useOrders';
import { OrderActions } from './OrderActions';
import { MatchCustomerModal } from './MatchCustomerModal';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import { usePageTitleSegment } from '@/lib/useDocumentTitle';

type LineItem = {
  // Typed shape served from commerce_orders_line_items (api #67 / api#103).
  line_sequence?: number | null;
  variant_name?: string | null;
  unit_price_cents?: number | null;
  line_total_cents?: number | null;
  sku?: string | null;
  // Sage ItemCode, when the line has been resolved to one. This is DISTINCT
  // from `sku` (the raw Faire SKU, which may not match a Sage ItemCode). Only
  // this field is safe to link to the product detail page. The API may serve it
  // as `item_code` or `sage_item_code`; both are read. Absent today on Faire
  // lines, so the link renders only when the API starts populating it.
  item_code?: string | null;
  sage_item_code?: string | null;
  product_name?: string | null;
  description?: string | null;
  quantity?: number | null;
  unit_price?: number | null;
  extended_price?: number | null;
  conversion_factor?: number | null;
  original_qty?: number | null;
  // Legacy metadata.lines fallback shapes vary; render best-effort.
  [key: string]: unknown;
};

// The Sage ItemCode for a line, when present and plausible. Never the raw Faire
// SKU: `sku` is the channel SKU and may not resolve to a Sage product, so it is
// deliberately excluded. Returns null when there is no valid Sage code to link.
function lineItemSageCode(li: LineItem): string | null {
  const raw = pickString(li.item_code) ?? pickString(li.sage_item_code);
  if (!raw) return null;
  const code = raw.trim();
  // Guard against path-like or over-long junk before building a route.
  if (!code || code.length > 30 || code.includes('/') || code.includes('\\') || code.includes('..')) {
    return null;
  }
  return code;
}

export function CommerceOrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSession();
  const groups = user?.groups ?? [];
  const isViewer = hasFaireViewer(groups, user?.superAdmin);
  const isAdmin = hasFaireAdmin(groups, user?.superAdmin);

  const orderQ = useOrderDetail(orderId ?? null);
  const shipOne = useShipOne();
  const del = useDeleteOrder();
  const [toast, setToast] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const order = orderQ.data ?? null;
  const orderHeading = order ? formatOrderNumber(order.source_order_id) : orderId;
  usePageTitleSegment(orderHeading);

  // "Back to list" preserves filters when the user got here from the list
  // page (most common). When opened via direct URL, fall back to a default.
  const backToList = () => {
    // Prefer history (it carries the URL search params), fall back to the
    // unfiltered list.
    if (typeof window !== 'undefined' && window.history.length > 1 && location.key !== 'default') {
      navigate(-1);
    } else {
      navigate('/commerce/orders');
    }
  };

  if (!isViewer) {
    return (
      <div className="pb-20">
        <Link
          to="/commerce/orders"
          className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          <ArrowLeft className="h-3 w-3" /> Back to orders
        </Link>
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink">No access.</p>
      </div>
    );
  }

  const faireUrl = order ? faireOrderUrl(order.source_channel, order.source_order_id) : null;

  const onShip = (id: string) => {
    setToast(null);
    shipOne.mutate(id, {
      onSuccess: (res) =>
        setToast(res.ok ? 'Shipped.' : `Ship failed: ${res.error ?? 'unknown'}`),
      onError: (e) => setToast(`Ship failed: ${e.message}`),
    });
  };

  return (
    <div className="pb-20">
      <button
        type="button"
        onClick={backToList}
        className="text-sm text-amp-ink-muted hover:text-amp-ink inline-flex items-center gap-1 mb-3 dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to orders
      </button>

      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">
            Commerce - Orders
          </p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink flex items-center gap-2">
            {orderHeading}
            {faireUrl && (
              <a
                href={faireUrl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open in Faire"
                aria-label="Open in Faire brand portal"
                className="text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
              >
                <ExternalLink className="h-5 w-5" aria-hidden />
              </a>
            )}
          </h1>
        </div>
        {order && (
          <OrderActions
            row={order}
            size="md"
            isAdmin={isAdmin}
            isEditor={isViewer}
            shippingOrderId={
              shipOne.isPending && typeof shipOne.variables === 'string'
                ? shipOne.variables
                : null
            }
            onMatch={() => setMatchOpen(true)}
            onShip={(id) => onShip(id)}
          />
        )}
      </div>

      {toast && (
        <div className="mb-4 rounded-md border border-amp-line px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:text-amp-dark-ink">
          {toast}
        </div>
      )}

      {orderQ.isLoading ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
      ) : orderQ.error ? (
        <p className="text-sm text-red-700 dark:text-red-300">
          Could not load: {orderQ.error.message}
        </p>
      ) : !order ? (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Order not found.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <Card title="Order">
              <Row label="Channel">{order.source_channel}</Row>
              <Row label="Brand">{order.brand}</Row>
              <Row label="Order date">{formatDate(order.order_date)}</Row>
              <Row label="Ship by">{formatDate(order.required_ship_date)}</Row>
              {order.shipped_at && <Row label="Shipped">{formatDate(order.shipped_at)}</Row>}
              {order.delivered_at && <Row label="Delivered">{formatDate(order.delivered_at)}</Row>}
              <Row label="Status">{ORDER_STATUS_LABELS[order.status] ?? order.status}</Row>
              <Row label="Ship status">{SHIP_STATUS_LABELS[order.ship_status] ?? order.ship_status}</Row>
              <Row label="Total">{formatMoney(order.total_amount)}</Row>
              <Row label="Created">{formatDateTime(order.created_at)}</Row>
              <Row label="Updated">{formatDateTime(order.updated_at)}</Row>
              {order.created_by && <Row label="Created by">{order.created_by}</Row>}
              <DevIdDisclosure id={order.order_id} />
            </Card>
            <PartnerCard partner={order.partner} fallbackName={order.partner_name} isFaire={order.source_channel === 'faire'} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <Card title="Sage">
              <Row label="Sales Order">{order.sage_so_number ?? '--'}</Row>
              <Row label="Invoice">{order.sage_invoice_no ?? '--'}</Row>
              {/* tracking_number holds a comma-separated list, one entry per
                  package (api #89). Render each package on its own line. */}
              <Row label="Tracking">
                {order.tracking_number
                  ? order.tracking_number.split(',').map((t) => (
                      <span key={t.trim()} className="block">{t.trim()}</span>
                    ))
                  : '--'}
              </Row>
              <Row label="Carrier">{order.carrier ?? '--'}</Row>
            </Card>
            <Card title="Totals">
              <TotalsBlock
                metadata={order.metadata}
                lineItems={(order.line_items ?? null) as LineItem[] | null}
                totalAmount={order.total_amount}
              />
            </Card>
          </div>

          <AddressSection shipTo={order.ship_to} billTo={order.bill_to} partner={order.partner} />

          <LineItemsCard
            items={(order.line_items ?? null) as LineItem[] | null}
            fromMetadata={order._line_items_from_metadata ?? false}
            orderId={order.order_id}
          />
        </>
      )}

      {confirmDelete && order && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" role="dialog">
          <div className="w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
            <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-2">
              Delete order?
            </h2>
            <p className="text-sm text-amp-ink dark:text-amp-dark-ink mb-4">
              {formatOrderNumber(order.source_order_id)} will be soft-deleted from Cockpit. The upstream record in
              Faire / Sage is unaffected. This cannot be undone from the UI.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(false)}
                className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
              >
                Keep
              </button>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  del.mutate(order.order_id, {
                    onSuccess: () => navigate('/commerce/orders'),
                    onError: (e) => setToast(`Delete failed: ${e.message}`),
                  });
                }}
                disabled={del.isPending}
                className="text-xs border border-red-700 bg-red-700 text-amp-white rounded px-3 py-1.5 hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {del.isPending ? 'Deleting...' : 'Confirm delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {matchOpen && order && (
        <MatchCustomerModal
          orderId={order.order_id}
          sourceOrderId={order.source_order_id}
          onClose={() => setMatchOpen(false)}
          onMatched={(_no, name) => {
            setToast(name ? `Matched to ${name}.` : 'Customer matched.');
          }}
        />
      )}

      {order && (
        <footer className="mt-6 flex items-center justify-between gap-3 border-t border-amp-line pt-4 dark:border-amp-dark-line">
          <button
            type="button"
            onClick={backToList}
            className="text-xs border border-amp-line rounded px-3 py-1.5 text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
          >
            Cancel
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setConfirmDelete(true)}
              className="inline-flex items-center gap-1 text-xs border border-red-300 text-red-700 rounded px-3 py-1.5 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-900/30"
            >
              <Trash2 className="h-3 w-3" />
              Delete
            </button>
          )}
        </footer>
      )}
    </div>
  );
}

function PartnerCard({
  partner,
  fallbackName,
  isFaire,
}: {
  partner: PartnerEnrichment | null;
  fallbackName: string | null;
  isFaire: boolean;
}) {
  if (!partner) {
    return (
      <Card title="Partner">
        <Row label="Name">{fallbackName ?? '--'}</Row>
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-2">
          No Sage customer linked yet. Use Match to link this partner.
        </p>
      </Card>
    );
  }
  const address = [partner.address_line_1, partner.city, partner.state, partner.zip]
    .filter(Boolean)
    .join(', ');
  return (
    <Card title="Partner">
      <Row label="Name">{partner.customer_name ?? fallbackName ?? '--'}</Row>
      <Row label="Sage CustomerNo">
        <span className="font-mono">{partner.sage_customer_no}</span>
      </Row>
      <Row label="Account address (Sage)">{address || '--'}</Row>
      <Row label="Phone">{partner.phone ?? '--'}</Row>
      <Row label="Terms">{isFaire ? '44' : (partner.terms_code ?? '--')}</Row>
      <Row label="Salesperson">{isFaire ? 'HAMM' : (partner.salesperson_no ?? '--')}</Row>
      {isFaire && (
        <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-2">
          Faire orders are sent to Sage with fixed Terms 44 / Salesperson HAMM, overriding partner defaults.
        </p>
      )}
      <Row label="Last invoice">
        {formatDate(partner.last_invoice_date)}
      </Row>
      <Row label="Lifetime invoices">{String(partner.lifetime_invoice_count)}</Row>
      <Row label="YTD invoices">{String(partner.ytd_invoice_count)}</Row>
      <Row label="YTD freight">{formatMoney(partner.ytd_freight)}</Row>
    </Card>
  );
}

function TotalsBlock({
  metadata,
  lineItems,
  totalAmount,
}: {
  metadata: unknown;
  lineItems: LineItem[] | null;
  totalAmount: string | null;
}) {
  const m = (metadata && typeof metadata === 'object' ? (metadata as Record<string, unknown>) : {}) || {};
  const net = pickNumber(m.net_order_total) ?? toNumber(totalAmount);
  const freight = pickNumber(m.freight);
  // Typed line_items (commerce_orders_line_items via the api) are the source
  // of truth; metadata.lines is the safety-window fallback only.
  const lines: unknown[] = lineItems && lineItems.length > 0
    ? lineItems
    : Array.isArray(m.lines) ? m.lines : [];
  const lineCount = lines.length;
  const lineSum = lines.reduce<number>((acc, li) => {
    const ext = pickNumber((li as Record<string, unknown>).extended_price);
    if (ext !== null) return acc + ext;
    const q = pickNumber((li as Record<string, unknown>).quantity);
    const up = pickNumber((li as Record<string, unknown>).unit_price);
    if (q !== null && up !== null) return acc + q * up;
    return acc;
  }, 0);

  return (
    <>
      <Row label="Line items">{String(lineCount)}</Row>
      <Row label="Line total">{lineCount > 0 ? formatMoney(lineSum) : '--'}</Row>
      <Row label="Freight">{formatMoney(freight ?? null)}</Row>
      <Row label="Order total">{formatMoney(net ?? null)}</Row>
    </>
  );
}

function LineItemsCard({ items, fromMetadata, orderId }: { items: LineItem[] | null; fromMetadata?: boolean; orderId: string }) {
  if (!items || items.length === 0) {
    return (
      <div className="rounded-lg border border-amp-line p-4 dark:border-amp-dark-line">
        <h3 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
          Line items
        </h3>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          No line items recorded for this order.
        </p>
      </div>
    );
  }
  const hasCaseFactor = items.some(li => (pickNumber(li.conversion_factor) ?? 0) > 1);
  return (
    <div className="rounded-lg border border-amp-line dark:border-amp-dark-line overflow-hidden">
      <div className="px-4 py-3 border-b border-amp-line dark:border-amp-dark-line">
        <h3 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
          Line items ({items.length})
        </h3>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-amp-cream-warm text-amp-ink-muted dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink-muted">
          <tr>
            <th className="text-left font-medium px-4 py-2">SKU</th>
            <th className="text-left font-medium px-4 py-2">Description</th>
            <th className="text-right font-medium px-4 py-2">{hasCaseFactor ? 'Qty (cases)' : 'Qty'}</th>
            <th className="text-right font-medium px-4 py-2">Unit price</th>
            <th className="text-right font-medium px-4 py-2">Extended</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
          {items.map((li, i) => {
            const sku = pickString(li.sku) ?? pickString(li.product_name) ?? '--';
            const sageCode = lineItemSageCode(li);
            // The /FAIRE line is the Sage comment (packing-slip note) the poller
            // appends to every Faire order; it is stored with no description, so
            // give it a friendly label + auto badge instead of a blank cell.
            const isFaireComment = sku === '/FAIRE';
            const desc = isFaireComment
              ? 'Sage comment line - added automatically at import (packing-slip note)'
              : pickString(li.description) ?? pickString(li.product_name) ?? '';
            const qty = pickNumber(li.quantity);
            const unit = pickNumber(li.unit_price);
            const ext = pickNumber(li.extended_price) ?? (qty !== null && unit !== null ? qty * unit : null);
            const cf = pickNumber(li.conversion_factor);
            const unitCount = cf !== null && cf > 1
              ? (pickNumber(li.original_qty) ?? (qty !== null ? Math.round(qty * cf) : null))
              : null;
            return (
              <tr key={`${sku}-${i}`}>
                <td className="px-4 py-2 font-mono text-xs text-amp-ink dark:text-amp-dark-ink">
                  {sageCode ? (
                    <Link
                      to={`/commerce/products/${encodeURIComponent(sageCode)}?from=order&order=${encodeURIComponent(orderId)}`}
                      className="hover:underline"
                    >
                      {sku}
                    </Link>
                  ) : (
                    sku
                  )}
                </td>
                <td className="px-4 py-2 text-amp-ink dark:text-amp-dark-ink">
                  {desc}
                  {isFaireComment && (
                    <span className="ml-2 inline-block rounded-full border border-amp-line px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-amp-ink-muted dark:border-amp-dark-line dark:text-amp-dark-ink-muted">auto</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                  {qty ?? '--'}
                  {unitCount !== null && (
                    <span className="ml-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      ({unitCount} units)
                    </span>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                  {formatMoney(unit ?? null)}
                </td>
                <td className="px-4 py-2 text-right text-amp-ink dark:text-amp-dark-ink">
                  {formatMoney(ext ?? null)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {fromMetadata && (
        <div className="px-4 py-2 border-t border-amp-line dark:border-amp-dark-line">
          <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            quantities shown in units
          </p>
        </div>
      )}
    </div>
  );
}

function formatCityStateZip(city: string | null, state: string | null, zip: string | null): string {
  const left = [city, state].filter((s): s is string => !!s && !!s.trim()).join(', ');
  const z = zip && zip.trim() ? zip.trim() : '';
  if (left && z) return `${left} ${z}`;
  return left || z;
}

function addressMatchesPartner(addr: ChannelAddress, partner: PartnerEnrichment): boolean {
  const norm = (s: string | null) => (s ?? '').trim().toUpperCase();
  return (
    norm(addr.line1) === norm(partner.address_line_1) &&
    norm(addr.city) === norm(partner.city) &&
    norm(addr.state) === norm(partner.state) &&
    norm(addr.zip) === norm(partner.zip)
  );
}

function AddressBlock({ title, addr }: { title: string; addr: ChannelAddress }) {
  const cityStateZip = formatCityStateZip(addr.city, addr.state, addr.zip);
  const headline = addr.company_name || addr.name || null;
  const sub = addr.company_name && addr.name && addr.company_name !== addr.name ? addr.name : null;
  return (
    <div className="rounded-lg border border-amp-line p-4 dark:border-amp-dark-line">
      <h3 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">{title}</h3>
      <div className="space-y-0.5 text-sm">
        {headline && (
          <div className="font-medium text-amp-ink dark:text-amp-dark-ink">{headline}</div>
        )}
        {sub && (
          <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted">{sub}</div>
        )}
        {addr.line1 && (
          <div className="text-amp-ink dark:text-amp-dark-ink">{addr.line1}</div>
        )}
        {addr.line2 && (
          <div className="text-amp-ink dark:text-amp-dark-ink">{addr.line2}</div>
        )}
        {cityStateZip && (
          <div className="text-amp-ink dark:text-amp-dark-ink">{cityStateZip}</div>
        )}
        {addr.country && addr.country.toUpperCase() !== 'USA' && (
          <div className="text-amp-ink dark:text-amp-dark-ink">{addr.country}</div>
        )}
        {addr.phone && (
          <div className="text-amp-ink-muted dark:text-amp-dark-ink-muted mt-1">{addr.phone}</div>
        )}
      </div>
    </div>
  );
}

function AddressSection({
  shipTo,
  billTo,
  partner,
}: {
  shipTo: ChannelAddress | null;
  billTo: ChannelAddress | null;
  partner: PartnerEnrichment | null;
}) {
  if (!shipTo && !billTo) return null;
  const showBilling =
    billTo !== null && (partner === null || !addressMatchesPartner(billTo, partner));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      {shipTo && <AddressBlock title="Shipping address" addr={shipTo} />}
      {showBilling && billTo && <AddressBlock title="Billing address" addr={billTo} />}
    </div>
  );
}

function DevIdDisclosure({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted select-none">
        Dev details
      </summary>
      <div className="mt-1.5 flex items-center gap-2">
        <span className="font-mono text-xs text-amp-ink dark:text-amp-dark-ink break-all">{id}</span>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </details>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-amp-line p-4 dark:border-amp-dark-line">
      <h3 className="text-sm font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">{title}</h3>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-32 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted">{label}</span>
      <span className="flex-1 text-amp-ink dark:text-amp-dark-ink break-words">{children}</span>
    </div>
  );
}

function pickNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function pickString(v: unknown): string | null {
  if (typeof v === 'string' && v.trim().length > 0) return v;
  return null;
}

function toNumber(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

